<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { mixin as clickaway } from 'vue3-click-away';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';

const props = defineProps({
  existingTemplate: { type: Object, default: null },
  isSubmitting: { type: Boolean, default: false },
  inboxId: { type: [Number, String], required: true },
});

const emit = defineEmits(['submit', 'cancel']);

const store = useStore();

const { t, locale } = useI18n();
const isEditing = computed(() => !!props.existingTemplate);

const WHATSAPP_LANGUAGES = [
  { label: 'English', value: 'en' },
  { label: 'English (US)', value: 'en_US' },
  { label: 'English (UK)', value: 'en_GB' },
  { label: 'Spanish', value: 'es' },
  { label: 'Spanish (Latin America)', value: 'es_LA' },
  { label: 'Spanish (Argentina)', value: 'es_AR' },
  { label: 'Spanish (Mexico)', value: 'es_MX' },
  { label: 'Portuguese (Brazil)', value: 'pt_BR' },
  { label: 'Portuguese (Portugal)', value: 'pt_PT' },
  { label: 'French', value: 'fr' },
  { label: 'German', value: 'de' },
  { label: 'Italian', value: 'it' },
  { label: 'Dutch', value: 'nl' },
  { label: 'Russian', value: 'ru' },
  { label: 'Arabic', value: 'ar' },
  { label: 'Hindi', value: 'hi' },
  { label: 'Indonesian', value: 'id' },
  { label: 'Japanese', value: 'ja' },
  { label: 'Korean', value: 'ko' },
  { label: 'Chinese (Simplified)', value: 'zh_CN' },
  { label: 'Chinese (Traditional)', value: 'zh_TW' },
  { label: 'Turkish', value: 'tr' },
  { label: 'Thai', value: 'th' },
  { label: 'Vietnamese', value: 'vi' },
  { label: 'Polish', value: 'pl' },
  { label: 'Hebrew', value: 'he' },
];

const CATEGORIES = [
  { label: 'Marketing', value: 'MARKETING' },
  { label: 'Utility', value: 'UTILITY' },
  { label: 'Authentication', value: 'AUTHENTICATION' },
];

const HEADER_TYPES = [
  { label: 'None', value: 'NONE' },
  { label: 'Text', value: 'TEXT' },
  { label: 'Image', value: 'IMAGE' },
  { label: 'Video', value: 'VIDEO' },
  { label: 'Document', value: 'DOCUMENT' },
];

const BUTTON_TYPES = computed(() => {
  const types = [
    { label: 'Visit Website', value: 'URL' },
    { label: 'Call Phone Number', value: 'PHONE_NUMBER' },
    { label: 'Quick Reply', value: 'QUICK_REPLY' },
  ];
  if (category.value === 'MARKETING') {
    types.push({ label: 'Copy Offer Code', value: 'COPY_CODE' });
  }
  return types;
});

const getDefaultLanguage = () => {
  const currentLocale = locale.value || 'en';
  if (WHATSAPP_LANGUAGES.find(l => l.value === currentLocale))
    return currentLocale;
  const prefix = currentLocale.split('-')[0].split('_')[0];
  const match = WHATSAPP_LANGUAGES.find(l => l.value === prefix);
  return match ? match.value : 'en';
};

// Form state
const templateName = ref('');
const category = ref('UTILITY');
const language = ref(getDefaultLanguage());
const parameterFormat = ref('POSITIONAL');
const headerType = ref('NONE');
const headerText = ref('');
const headerMediaUrl = ref('');
const headerTextExample = ref('');
const headerMediaFile = ref(null);
const headerMediaPreviewUrl = ref('');
const headerMediaHandle = ref('');
const isUploadingMedia = ref(false);
const bodyText = ref('');
const bodyTextarea = ref(null);
const showEmojiPicker = ref(false);
const bodyExamples = ref([]);
const footerText = ref('');
const showFooter = ref(false);
const buttons = ref([]);

const ACCEPTED_FILE_TYPES = {
  IMAGE: 'image/jpeg,image/jpg,image/png',
  VIDEO: 'video/mp4',
  DOCUMENT: 'application/pdf',
};

const handleFileSelect = event => {
  const file = event.target.files[0];
  if (!file) return;
  headerMediaFile.value = file;
  headerMediaHandle.value = '';
  if (headerMediaPreviewUrl.value) {
    URL.revokeObjectURL(headerMediaPreviewUrl.value);
  }
  headerMediaPreviewUrl.value = URL.createObjectURL(file);
};

const removeFile = () => {
  headerMediaFile.value = null;
  headerMediaHandle.value = '';
  if (headerMediaPreviewUrl.value) {
    URL.revokeObjectURL(headerMediaPreviewUrl.value);
    headerMediaPreviewUrl.value = '';
  }
};

// Clean up object URL on unmount
onBeforeUnmount(() => {
  if (headerMediaPreviewUrl.value) {
    URL.revokeObjectURL(headerMediaPreviewUrl.value);
  }
});

// Reset file when header type changes
watch(headerType, () => {
  removeFile();
});

// Populate from existing template
const initializeFromTemplate = tmpl => {
  if (!tmpl) return;
  templateName.value = tmpl.name || '';
  category.value = tmpl.category || 'UTILITY';
  language.value = tmpl.language || 'en';
  parameterFormat.value = tmpl.parameter_format || 'POSITIONAL';
  const components = tmpl.components || [];
  const header = components.find(c => c.type === 'HEADER');
  if (header) {
    headerType.value = header.format || 'TEXT';
    if (header.format === 'TEXT') {
      headerText.value = header.text || '';
      headerTextExample.value = header.example?.header_text?.[0] || '';
    } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.format)) {
      headerMediaUrl.value = header.example?.header_handle?.[0] || '';
    }
  }
  const body = components.find(c => c.type === 'BODY');
  if (body) {
    bodyText.value = body.text || '';
    if (body.example?.body_text?.[0]) {
      bodyExamples.value = [...body.example.body_text[0]];
    } else if (body.example?.body_text_named_params) {
      bodyExamples.value = body.example.body_text_named_params.map(
        p => p.example || ''
      );
    }
  }
  const footer = components.find(c => c.type === 'FOOTER');
  if (footer) {
    showFooter.value = true;
    footerText.value = footer.text || '';
  }
  const buttonsComp = components.find(c => c.type === 'BUTTONS');
  if (buttonsComp?.buttons) {
    buttons.value = buttonsComp.buttons.map(btn => ({
      type: btn.type,
      text: btn.text || '',
      url: btn.url || '',
      phone_number: btn.phone_number || '',
      example: btn.example?.[0] || '',
    }));
  }
};

watch(
  () => props.existingTemplate,
  tmpl => initializeFromTemplate(tmpl),
  { immediate: true }
);

// Variable detection - use safe delimiters for display
const OPEN_BRACE = '{{';
const CLOSE_BRACE = '}}';
const varRegex = /\{\{([^}]+)\}\}/g;

const bodyVariables = computed(() => {
  const matches = bodyText.value.match(varRegex) || [];
  return matches.map(m => m.replace(/\{\{|\}\}/g, ''));
});

const headerVariables = computed(() => {
  if (headerType.value !== 'TEXT') return [];
  const matches = headerText.value.match(varRegex) || [];
  return matches.map(m => m.replace(/\{\{|\}\}/g, ''));
});

// Format variable for display without Vue interpolation issues
const wrapVar = v => OPEN_BRACE + v + CLOSE_BRACE;

watch(bodyVariables, vars => {
  while (bodyExamples.value.length < vars.length) {
    bodyExamples.value.push('');
  }
  if (bodyExamples.value.length > vars.length) {
    bodyExamples.value = bodyExamples.value.slice(0, vars.length);
  }
});

const nameError = computed(() => {
  if (!templateName.value) return '';
  if (!/^[a-z0-9_]+$/.test(templateName.value)) {
    return t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME.HELP');
  }
  return '';
});

const showButtonDropdown = ref(false);

const hideButtonDropdown = () => {
  showButtonDropdown.value = false;
};

const addButton = type => {
  if (buttons.value.length >= 10) return;
  buttons.value.push({
    type: type,
    text: '',
    url: '',
    url_type: 'STATIC',
    phone_number: '',
    example: '',
  });
  showButtonDropdown.value = false;
};

const removeButton = idx => {
  buttons.value.splice(idx, 1);
};

const isFormValid = computed(() => {
  if (!templateName.value || nameError.value) return false;
  if (!category.value || !language.value) return false;
  if (!bodyText.value) return false;
  return true;
});

const buildComponents = (mediaHandle = null) => {
  const components = [];
  if (headerType.value !== 'NONE') {
    const hdr = { type: 'HEADER', format: headerType.value };
    if (headerType.value === 'TEXT') {
      hdr.text = headerText.value;
      if (headerVariables.value.length > 0 && headerTextExample.value) {
        hdr.example = { header_text: [headerTextExample.value] };
      }
    } else if (mediaHandle) {
      hdr.example = { header_handle: [mediaHandle] };
    } else if (headerMediaHandle.value) {
      hdr.example = { header_handle: [headerMediaHandle.value] };
    }
    components.push(hdr);
  }
  const bdy = { type: 'BODY', text: bodyText.value };
  if (bodyVariables.value.length > 0 && bodyExamples.value.some(e => e)) {
    if (parameterFormat.value === 'NAMED') {
      bdy.example = {
        body_text_named_params: bodyVariables.value.map((v, i) => ({
          param_name: v,
          example: bodyExamples.value[i] || '',
        })),
      };
    } else {
      bdy.example = { body_text: [bodyExamples.value] };
    }
  }
  components.push(bdy);
  if (footerText.value) {
    components.push({ type: 'FOOTER', text: footerText.value });
  }
  if (buttons.value.length > 0) {
    const btns = buttons.value.map(btn => {
      const b = { type: btn.type, text: btn.text };
      if (btn.type === 'URL') {
        if (btn.url_type === 'DYNAMIC') {
          b.url = `${btn.url}{{1}}`;
          if (btn.example) {
            let exampleVal = btn.example.trim();
            // If the user typed the full URL (with or without https://),
            // extract only the dynamic part, as Meta's API strictly requires just the parameter.
            if (btn.url) {
              const normalizedBase = btn.url.replace(/^https?:\/\//, '');
              const normalizedExample = exampleVal.replace(/^https?:\/\//, '');
              if (
                normalizedBase &&
                normalizedExample.startsWith(normalizedBase)
              ) {
                exampleVal = normalizedExample.substring(normalizedBase.length);
              }
            }
            b.example = [exampleVal];
          }
        } else {
          b.url = btn.url;
        }
      }
      if (btn.type === 'PHONE_NUMBER') b.phone_number = btn.phone_number;
      return b;
    });
    components.push({ type: 'BUTTONS', buttons: btns });
  }
  return components;
};

const handleSubmit = async () => {
  if (!isFormValid.value) return;

  let mediaHandle = null;

  // Upload file to Meta if a file was selected
  if (
    ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType.value) &&
    headerMediaFile.value &&
    !headerMediaHandle.value
  ) {
    try {
      isUploadingMedia.value = true;
      const result = await store.dispatch(
        'inboxes/uploadWhatsAppTemplateMedia',
        { inboxId: props.inboxId, file: headerMediaFile.value }
      );
      mediaHandle = result.handle;
      headerMediaHandle.value = mediaHandle;
    } catch (error) {
      const errorMsg =
        error?.response?.data?.error ||
        t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER.UPLOAD_ERROR');
      useAlert(errorMsg);
      return;
    } finally {
      isUploadingMedia.value = false;
    }
  }

  emit('submit', {
    name: templateName.value,
    category: category.value,
    language: language.value,
    parameter_format: parameterFormat.value,
    components: buildComponents(mediaHandle),
  });
};

// Preview computeds
const previewHeader = computed(() => {
  if (headerType.value === 'NONE') return null;
  if (headerType.value === 'TEXT') {
    let txt = headerText.value;
    headerVariables.value.forEach(v => {
      const ex = headerTextExample.value || v;
      txt = txt.replace(wrapVar(v), ex);
    });
    return { type: 'TEXT', text: txt };
  }
  const previewUrl = headerMediaPreviewUrl.value || headerMediaUrl.value;
  return { type: headerType.value, url: previewUrl };
});

const previewBody = computed(() => {
  let txt = bodyText.value;
  bodyVariables.value.forEach((v, i) => {
    const ex = bodyExamples.value[i] || v;
    txt = txt.replace(wrapVar(v), ex);
  });
  return txt;
});

// WhatsApp-style text formatting for preview
function formatWhatsAppText(text) {
  if (!text) return '';
  // Escape HTML to prevent XSS
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // Monospace: ```text```
  html = html.replace(
    /```([^`]+)```/g,
    '<code class="font-mono text-[12px] bg-black/5 dark:bg-white/10 rounded px-1 py-0.5">$1</code>'
  );
  // Bold: *text*
  html = html.replace(/\*(.*?)\*/g, '<strong>$1</strong>');
  // Italic: _text_
  html = html.replace(/_(.*?)_/g, '<em>$1</em>');
  // Strikethrough: ~text~
  html = html.replace(/~(.*?)~/g, '<del>$1</del>');
  return html;
}

const formattedPreviewBody = computed(() => {
  return formatWhatsAppText(previewBody.value);
});

// Body formatting toolbar helpers
function wrapSelection(before, after) {
  const el = bodyTextarea.value;
  if (!el) return;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const text = bodyText.value;
  const selected = text.substring(start, end);
  const replacement = before + selected + after;
  bodyText.value = text.substring(0, start) + replacement + text.substring(end);
  // Restore cursor position after the wrapped text
  const cursorPos = start + before.length + selected.length + after.length;
  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(cursorPos, cursorPos);
  });
}

function insertBold() {
  wrapSelection('*', '*');
}
function insertItalic() {
  wrapSelection('_', '_');
}
function insertStrikethrough() {
  wrapSelection('~', '~');
}
function insertMonospace() {
  wrapSelection('```', '```');
}

function insertVariable() {
  const el = bodyTextarea.value;
  if (!el) return;
  const pos = el.selectionStart;
  const text = bodyText.value;
  let varText;
  if (parameterFormat.value === 'POSITIONAL') {
    // Count existing positional variables and get next number
    const existing = text.match(/\{\{\d+\}\}/g) || [];
    const nextNum = existing.length + 1;
    varText = `{{${nextNum}}}`;
  } else {
    varText = '{{}}';
  }
  bodyText.value = text.substring(0, pos) + varText + text.substring(pos);
  const cursorPos = pos + varText.length;
  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(cursorPos, cursorPos);
  });
}

function hideEmojiPicker() {
  showEmojiPicker.value = false;
}

function insertEmoji(emoji) {
  const el = bodyTextarea.value;
  if (!el) return;
  const pos = el.selectionStart;
  const text = bodyText.value;
  bodyText.value = text.substring(0, pos) + emoji + text.substring(pos);
  const cursorPos = pos + emoji.length;
  showEmojiPicker.value = false;
  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(cursorPos, cursorPos);
  });
}
</script>

<template>
  <div
    class="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_320px] lg:grid-cols-[minmax(0,1fr)_360px] gap-6 items-start"
  >
    <!-- Form Column -->
    <div class="flex flex-col gap-5">
      <h3 class="text-lg font-semibold text-n-slate-12">
        {{
          isEditing
            ? $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EDIT_TITLE')
            : $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CREATE_TITLE')
        }}
      </h3>

      <p
        v-if="isEditing"
        class="text-xs text-n-amber-11 bg-n-amber-3 px-3 py-2 rounded-lg"
      >
        {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.EDIT_LIMITS.NOTE') }}
      </p>

      <!-- Section: Name & Language -->
      <div
        class="flex flex-col gap-4 p-5 rounded-xl border border-n-weak bg-white dark:bg-n-solid-1 shadow-sm"
      >
        <h4 class="text-base font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME.LABEL') }}
        </h4>
        <div class="flex flex-col gap-1">
          <input
            v-model="templateName"
            type="text"
            :disabled="isEditing"
            maxlength="512"
            :placeholder="
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME.PLACEHOLDER')
            "
            class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
          <div class="flex items-center justify-between">
            <span v-if="nameError" class="text-xs text-n-ruby-10">
              {{ nameError }}
            </span>
            <span v-else class="text-xs text-n-slate-10">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME.HELP') }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ templateName.length }}{{ '/512' }}
            </span>
          </div>
        </div>

        <!-- Category + Language -->
        <div class="flex gap-4">
          <div class="flex-1 flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY.LABEL') }}
            </label>
            <div class="relative">
              <select
                v-model="category"
                :disabled="isEditing"
                class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand !bg-none appearance-none pr-10"
              >
                <option
                  v-for="cat in CATEGORIES"
                  :key="cat.value"
                  :value="cat.value"
                >
                  {{
                    $t(
                      `INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY.TYPE.${cat.value}`
                    )
                  }}
                </option>
              </select>
              <div
                class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-n-slate-11"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div class="flex-1 flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LANGUAGE.LABEL') }}
            </label>
            <div class="relative">
              <select
                v-model="language"
                :disabled="isEditing"
                class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand !bg-none appearance-none pr-10"
              >
                <option
                  v-for="lang in WHATSAPP_LANGUAGES"
                  :key="lang.value"
                  :value="lang.value"
                >
                  {{ lang.label }}
                </option>
              </select>
              <div
                class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-n-slate-11"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Parameter Format -->
        <div
          v-if="!isEditing"
          class="flex flex-col gap-1 border-t border-n-weak pt-4"
        >
          <label class="text-sm font-medium text-n-slate-12">
            {{
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.PARAMETER_FORMAT.LABEL')
            }}
          </label>
          <div class="relative">
            <select
              v-model="parameterFormat"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand !bg-none appearance-none pr-10"
            >
              <option value="POSITIONAL">
                {{
                  $t(
                    'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.PARAMETER_FORMAT.POSITIONAL'
                  )
                }}
              </option>
              <option value="NAMED">
                {{
                  $t(
                    'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.PARAMETER_FORMAT.NAMED'
                  )
                }}
              </option>
            </select>
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-n-slate-11"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Section: Content -->
      <div
        class="flex flex-col gap-4 p-5 rounded-xl border border-n-weak bg-white dark:bg-n-solid-1 shadow-sm"
      >
        <div>
          <h4 class="text-base font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER.LABEL') }}
            <span class="text-n-slate-10 text-sm font-normal">
              {{ '· Optional' }}
            </span>
          </h4>
          <div class="relative mt-2">
            <select
              v-model="headerType"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand !bg-none appearance-none pr-10"
            >
              <option
                v-for="ht in HEADER_TYPES"
                :key="ht.value"
                :value="ht.value"
              >
                {{
                  $t(
                    `INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER.TYPE.${ht.value}`
                  )
                }}
              </option>
            </select>
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-n-slate-11"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>
        <div v-if="headerType === 'TEXT'" class="flex flex-col gap-1">
          <input
            v-model="headerText"
            type="text"
            maxlength="60"
            :placeholder="
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER.TEXT_PLACEHOLDER')
            "
            class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
          <span class="text-xs text-n-slate-10 text-right">
            {{ headerText.length }}{{ '/60' }}
          </span>
          <input
            v-if="headerVariables.length > 0"
            v-model="headerTextExample"
            type="text"
            :placeholder="
              $t(
                'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER.EXAMPLE_PLACEHOLDER'
              )
            "
            class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-xs text-n-slate-11 placeholder:text-n-slate-9 focus:outline-none"
          />
        </div>
        <div
          v-if="['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType)"
          class="flex flex-col gap-2"
        >
          <!-- File selected: show preview and remove button -->
          <div
            v-if="headerMediaFile"
            class="flex items-center gap-3 rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2"
          >
            <img
              v-if="headerType === 'IMAGE' && headerMediaPreviewUrl"
              :src="headerMediaPreviewUrl"
              class="h-10 w-10 rounded object-cover"
              alt="Preview"
            />
            <span
              v-else-if="headerType === 'VIDEO'"
              class="i-lucide-film h-6 w-6 text-n-slate-11"
            />
            <span v-else class="i-lucide-file-text h-6 w-6 text-n-slate-11" />
            <span class="flex-1 truncate text-sm text-n-slate-12">
              {{ headerMediaFile.name }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ (headerMediaFile.size / 1024).toFixed(1) }} KB
            </span>
            <button
              type="button"
              class="text-n-slate-10 hover:text-n-ruby-9 transition-colors"
              @click="removeFile"
            >
              <span class="i-lucide-x h-4 w-4" />
            </button>
          </div>
          <!-- No file: show upload button -->
          <label
            v-else
            class="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-n-weak bg-n-solid-2 px-4 py-5 text-center transition-colors hover:border-n-brand hover:bg-n-alpha-1"
          >
            <span class="i-lucide-upload h-6 w-6 text-n-slate-10" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER.UPLOAD_LABEL') }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{
                $t(
                  `INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER.ACCEPTED_FORMATS.${headerType}`
                )
              }}
            </span>
            <input
              type="file"
              class="hidden"
              :accept="ACCEPTED_FILE_TYPES[headerType]"
              @change="handleFileSelect"
            />
          </label>
        </div>

        <!-- Body -->
        <div class="flex flex-col gap-1 border-t border-n-weak pt-4">
          <h4 class="text-base font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY.LABEL') }}
          </h4>
          <textarea
            ref="bodyTextarea"
            v-model="bodyText"
            rows="5"
            maxlength="1024"
            :placeholder="
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY.PLACEHOLDER')
            "
            class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand resize-y"
          />
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1">
              <!-- Emoji Button -->
              <div class="relative">
                <button
                  type="button"
                  class="p-1.5 rounded-md hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-slate-12 transition-colors"
                  title="Emoji"
                  @click="showEmojiPicker = !showEmojiPicker"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="1.5"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
                <EmojiInput
                  v-if="showEmojiPicker"
                  v-on-clickaway="hideEmojiPicker"
                  class="absolute bottom-full left-0 mb-2 z-20"
                  :on-click="insertEmoji"
                />
              </div>
              <!-- Bold -->
              <button
                type="button"
                class="p-1.5 rounded-md hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-slate-12 transition-colors font-bold text-sm"
                title="Bold (*text*)"
                @click="insertBold"
              >
                <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                B
              </button>
              <!-- Italic -->
              <button
                type="button"
                class="p-1.5 rounded-md hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-slate-12 transition-colors italic text-sm"
                title="Italic (_text_)"
                @click="insertItalic"
              >
                <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                I
              </button>
              <!-- Strikethrough -->
              <button
                type="button"
                class="p-1.5 rounded-md hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-slate-12 transition-colors line-through text-sm"
                title="Strikethrough (~text~)"
                @click="insertStrikethrough"
              >
                <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                S
              </button>
              <!-- Monospace -->
              <button
                type="button"
                class="p-1.5 rounded-md hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-slate-12 transition-colors font-mono text-sm"
                title="Monospace (```text```)"
                @click="insertMonospace"
              >
                <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                &lt;/&gt;
              </button>
              <!-- Add Variable -->
              <button
                type="button"
                class="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-n-alpha-2 text-n-brand text-sm font-medium transition-colors ml-1"
                @click="insertVariable"
              >
                <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                +
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY.ADD_VARIABLE') }}
              </button>
            </div>
            <span class="text-xs text-n-slate-10">
              {{ bodyText.length }}{{ '/1024' }}
            </span>
          </div>
          <div
            v-if="bodyVariables.length > 0"
            class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-1 border border-n-weak"
          >
            <span class="text-xs font-medium text-n-slate-11">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY.EXAMPLES_LABEL') }}
            </span>
            <div
              v-for="(variable, index) in bodyVariables"
              :key="variable"
              class="flex items-center gap-2"
            >
              <span class="text-xs text-n-slate-10 min-w-[4rem]">
                {{ wrapVar(variable) }}
              </span>
              <input
                v-model="bodyExamples[index]"
                type="text"
                :placeholder="
                  $t(
                    'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY.EXAMPLE_PLACEHOLDER'
                  ) +
                  ' ' +
                  variable
                "
                class="flex-1 rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1 text-xs text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none"
              />
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex flex-col gap-1 border-t border-n-weak pt-4">
          <h4 class="text-base font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER.LABEL') }}
            <span class="text-n-slate-10 text-sm font-normal">
              {{ '· Optional' }}
            </span>
          </h4>
          <input
            v-model="footerText"
            type="text"
            maxlength="60"
            :placeholder="
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER.PLACEHOLDER')
            "
            class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
          <span class="text-xs text-n-slate-10 text-right">
            {{ footerText.length }}{{ '/60' }}
          </span>
        </div>
      </div>

      <!-- Section: Buttons -->
      <div
        class="flex flex-col gap-3 p-5 rounded-xl border border-n-weak bg-white dark:bg-n-solid-1 shadow-sm"
      >
        <div class="flex items-start justify-between">
          <div class="flex flex-col">
            <h4 class="text-base font-semibold text-n-slate-12">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.LABEL') }}
              <span class="text-n-slate-10 text-sm font-normal">
                {{ '· Optional' }}
              </span>
            </h4>
            <p class="text-sm text-n-slate-11 mt-1">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.DESCRIPTION') }}
            </p>
          </div>
        </div>

        <div v-on-clickaway="hideButtonDropdown" class="relative mt-2">
          <button
            v-if="buttons.length < 10"
            type="button"
            class="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 text-sm font-medium text-n-slate-12 transition-colors w-max"
            @click="showButtonDropdown = !showButtonDropdown"
          >
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.ADD') }}
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 ml-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          <div
            v-if="showButtonDropdown"
            class="absolute left-0 top-full mt-1 w-64 bg-white dark:bg-n-solid-2 rounded-xl shadow-lg border border-n-weak py-1 z-20 flex flex-col"
          >
            <button
              v-for="bt in BUTTON_TYPES"
              :key="bt.value"
              type="button"
              class="px-4 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2 w-full flex items-center gap-2"
              @click="addButton(bt.value)"
            >
              <!-- Icons for specific button types -->
              <svg
                v-if="bt.value === 'URL'"
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-n-slate-11"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
              <svg
                v-else-if="bt.value === 'PHONE_NUMBER'"
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-n-slate-11"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              <svg
                v-else-if="bt.value === 'QUICK_REPLY'"
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-n-slate-11"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-n-slate-11"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              {{
                $t(
                  `INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.TYPE.${bt.value}`
                )
              }}
            </button>
          </div>
        </div>

        <!-- Rendered Buttons -->
        <div
          v-for="(button, index) in buttons"
          :key="index"
          class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-alpha-1 relative mt-2"
        >
          <!-- Delete button on top right of the card -->
          <button
            type="button"
            class="absolute top-2 right-2 p-1.5 text-n-slate-10 hover:text-n-slate-12 hover:bg-white dark:hover:bg-n-solid-2 rounded-md transition-colors"
            @click="removeButton(index)"
          >
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div class="flex items-start gap-4 w-full pr-8">
            <div class="flex flex-col gap-1 w-[35%]">
              <span class="text-xs font-semibold text-n-slate-11">
                {{
                  $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.TYPE.LABEL')
                }}
              </span>
              <div class="relative">
                <select
                  v-model="button.type"
                  class="w-full appearance-none rounded-lg border border-n-weak bg-white dark:bg-n-solid-2 px-3 py-2 pr-8 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
                >
                  <option
                    v-for="bt in BUTTON_TYPES"
                    :key="bt.value"
                    :value="bt.value"
                  >
                    {{
                      $t(
                        `INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.TYPE.${bt.value}`
                      )
                    }}
                  </option>
                </select>
                <div
                  class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-n-slate-11"
                >
                  <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div class="flex flex-col gap-1 flex-1 relative">
              <span class="text-xs font-semibold text-n-slate-11">
                {{
                  $t(
                    'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.TEXT_PLACEHOLDER'
                  )
                }}
              </span>
              <input
                v-model="button.text"
                type="text"
                maxlength="25"
                :placeholder="
                  $t(
                    'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.TEXT_PLACEHOLDER'
                  )
                "
                class="w-full rounded-lg border border-n-weak bg-white dark:bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
              <span
                class="absolute right-2 top-[34px] text-xs text-n-slate-9 bg-white dark:bg-n-solid-2 pl-1"
              >
                {{ button.text.length }}{{ '/25' }}
              </span>
            </div>

            <div
              v-if="button.type === 'URL'"
              class="flex flex-col gap-1 w-[20%]"
            >
              <span class="text-xs font-semibold text-n-slate-11">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.URL_TYPE') }}
              </span>
              <div class="relative">
                <select
                  v-model="button.url_type"
                  class="w-full appearance-none rounded-lg border border-n-weak bg-white dark:bg-n-solid-2 px-3 py-2 pr-8 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
                >
                  <option value="STATIC">
                    {{
                      $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.STATIC')
                    }}
                  </option>
                  <option value="DYNAMIC">
                    {{
                      $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.DYNAMIC')
                    }}
                  </option>
                </select>
                <div
                  class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-n-slate-11"
                >
                  <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <!-- Second row for URL or Phone Number input -->
          <div
            v-if="button.type === 'URL' || button.type === 'PHONE_NUMBER'"
            class="flex items-center w-full gap-4 mt-1 pr-8"
          >
            <div class="flex flex-col gap-1 w-full relative">
              <span class="text-xs font-semibold text-n-slate-11">
                {{
                  button.type === 'URL'
                    ? $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.URL_LABEL')
                    : $t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.PHONE_LABEL'
                      )
                }}
              </span>
              <div v-if="button.type === 'URL'" class="relative">
                <input
                  v-model="button.url"
                  type="url"
                  maxlength="2000"
                  :placeholder="
                    button.url_type === 'DYNAMIC'
                      ? 'https://example.com/path/'
                      : $t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.URL_PLACEHOLDER'
                        )
                  "
                  class="w-full rounded-lg border border-n-weak bg-white dark:bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand"
                />
                <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                <span
                  v-if="button.url_type === 'DYNAMIC'"
                  class="absolute right-10 top-[10px] text-xs font-mono text-n-slate-9 bg-white dark:bg-n-solid-2 px-1 pointer-events-none"
                  v-text="'{{1}}'"
                />
                <span
                  class="absolute right-2 top-[10px] text-xs text-n-slate-9 bg-white dark:bg-n-solid-2 pl-1"
                >
                  {{ button.url.length }}{{ '/2000' }}
                </span>
              </div>
              <input
                v-if="button.type === 'PHONE_NUMBER'"
                v-model="button.phone_number"
                type="tel"
                maxlength="20"
                :placeholder="
                  $t(
                    'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.PHONE_PLACEHOLDER'
                  )
                "
                class="w-full rounded-lg border border-n-weak bg-white dark:bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
              <span
                v-if="button.type === 'PHONE_NUMBER'"
                class="absolute right-2 top-[34px] text-xs text-n-slate-9 bg-white dark:bg-n-solid-2 pl-1"
              >
                {{ button.phone_number.length }}{{ '/20' }}
              </span>
            </div>
          </div>

          <!-- Dynamic URL example value -->
          <div
            v-if="button.type === 'URL' && button.url_type === 'DYNAMIC'"
            class="flex items-center w-full gap-4 mt-1 pr-8"
          >
            <div class="flex flex-col gap-1 w-full relative">
              <span class="text-xs font-semibold text-n-slate-11">
                {{
                  $t(
                    'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.DYNAMIC_EXAMPLE'
                  )
                }}
              </span>
              <div class="flex items-center gap-2">
                <span
                  class="text-sm font-semibold text-n-slate-11 w-10 text-center font-mono"
                  v-text="'{{1}}'"
                />
                <input
                  v-model="button.example"
                  type="text"
                  maxlength="2000"
                  :placeholder="
                    $t(
                      'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.DYNAMIC_EXAMPLE_PLACEHOLDER',
                      { url: button.url || 'https://ejemplo.com/ruta/' }
                    ) + '{{1}}'
                  "
                  class="w-full rounded-lg border border-n-weak bg-white dark:bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Submit -->
      <div
        class="flex items-center justify-end gap-3 pt-4 border-t border-n-weak"
      >
        <NextButton
          ghost
          slate
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CANCEL')"
          @click="$emit('cancel')"
        />
        <NextButton
          :label="
            isEditing
              ? $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.SUBMIT_EDIT')
              : $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.SUBMIT_CREATE')
          "
          :is-loading="isSubmitting || isUploadingMedia"
          :disabled="!isFormValid || isSubmitting || isUploadingMedia"
          @click="handleSubmit"
        />
      </div>
    </div>

    <!-- Preview Column -->
    <div class="w-full sticky top-4">
      <div
        class="flex flex-col rounded-xl border border-n-weak bg-white dark:bg-n-solid-1 shadow-sm overflow-hidden"
      >
        <!-- Meta Style Card Header -->
        <div
          class="px-5 py-4 border-b border-n-weak bg-white dark:bg-n-solid-1"
        >
          <h4 class="text-base font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.TITLE') }}
          </h4>
        </div>
        <!-- WhatsApp Background Area -->
        <div
          class="relative bg-[#efeae2] dark:bg-[#0b141a] p-6 flex flex-col items-start min-h-[300px]"
        >
          <!-- Background pattern overlay (subtle) -->
          <!-- eslint-disable-next-line vue/no-static-inline-styles -->
          <div
            class="absolute inset-0 opacity-[0.06] dark:opacity-[0.03] pointer-events-none"
            style="
              background-image: url('https://static.whatsapp.net/rsrc.php/v3/yl/r/r_QZ3OA1N_M.png');
              background-repeat: repeat;
            "
          />
          <!-- WhatsApp bubble -->
          <div
            class="relative z-10 max-w-[260px] rounded-lg bg-[#e7fed6] dark:bg-[#005c4b] p-3 shadow-sm flex flex-col gap-1"
          >
            <div v-if="previewHeader" class="mb-1">
              <p
                v-if="previewHeader.type === 'TEXT'"
                class="text-sm font-bold text-[#111b21] dark:text-[#e9edef]"
              >
                {{ previewHeader.text }}
              </p>
              <div
                v-else-if="previewHeader.type === 'IMAGE' && previewHeader.url"
                class="w-full rounded overflow-hidden"
              >
                <img
                  :src="previewHeader.url"
                  class="w-full h-auto object-cover max-h-48"
                  alt="Header Image"
                  @error="$event.target.style.display = 'none'"
                />
              </div>
              <div
                v-else-if="previewHeader.type === 'VIDEO' && previewHeader.url"
                class="w-full rounded overflow-hidden bg-black"
              >
                <video
                  :src="previewHeader.url"
                  controls
                  class="w-full h-auto max-h-48"
                  @error="$event.target.style.display = 'none'"
                />
              </div>
              <div
                v-else-if="
                  previewHeader.type === 'DOCUMENT' && previewHeader.url
                "
                class="w-full h-16 rounded bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 flex items-center px-3 gap-3"
              >
                <span
                  class="i-lucide-file-text text-2xl text-[#667781] dark:text-[#8696a0]"
                />
                <span
                  class="text-xs text-[#111b21] dark:text-[#e9edef] truncate"
                >
                  {{ previewHeader.url.split('/').pop() || 'Documento' }}
                </span>
              </div>
              <div
                v-else-if="previewHeader.type === 'LOCATION'"
                class="w-full h-32 rounded bg-[#e8e9eb] dark:bg-[#111b21] flex flex-col items-center justify-center gap-2"
              >
                <span class="i-lucide-map-pin text-3xl text-n-brand" />
                <span
                  class="text-xs font-semibold text-[#111b21] dark:text-[#e9edef]"
                >
                  Ubicación
                </span>
              </div>
              <div
                v-else
                class="w-full h-24 rounded bg-black/10 dark:bg-white/10 flex items-center justify-center"
              >
                <span class="text-xs text-[#667781] dark:text-[#8696a0]">
                  {{ previewHeader.type }}
                </span>
              </div>
            </div>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <p
              class="text-[13px] text-[#111b21] dark:text-[#e9edef] whitespace-pre-wrap break-words leading-5"
              v-html="formattedPreviewBody"
            />
            <div class="flex items-center justify-end gap-1 mt-0.5">
              <p
                v-if="footerText"
                class="text-[11px] text-[#667781] dark:text-[#8696a0] mr-auto"
              >
                {{ footerText }}
              </p>
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
              <span class="text-[11px] text-[#667781] dark:text-[#8696a0]">
                {{
                  new Date()
                    .toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })
                    .toLowerCase()
                }}
              </span>
            </div>
          </div>
          <!-- Button previews below bubble -->
          <div
            v-if="buttons.length > 0"
            class="relative z-10 max-w-[260px] w-full flex flex-col gap-1 mt-1"
          >
            <div
              v-for="(button, index) in buttons"
              :key="index"
              class="flex items-center justify-center gap-1 py-2 rounded-lg bg-white dark:bg-[#1f2c33] text-[13px] font-medium text-[#00a5f4] shadow-sm"
            >
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
              {{ button.text || 'Button' }}
            </div>
          </div>
        </div>
      </div>
      <div class="mt-3 text-center">
        <span class="text-xs text-n-slate-10">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.TOOLTIP') }}
        </span>
      </div>
    </div>
  </div>
</template>
